import { useState } from "react";
import useRequestScheduledCallback from "../hooks/useRequestScheduledCallback";
import { DateTime } from "luxon";
import parsePhoneNumber from "libphonenumber-js";
import {
  Container,
  Alert,
  DatePicker,
  TimeInput,
  Input,
  Button,
  Form,
  SpaceBetween,
  Header,
  FormField,
  RadioGroup,
} from "@cloudscape-design/components";
import environment from "../env";

function ScheduleForm() {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [fileNumber, setFileNumber] = useState("");
  const [accountType, setAccountType] = useState("other");
  const [errors, setErrors] = useState({});
  const { response, isLoading, isError, requestScheduledCallback } =
    useRequestScheduledCallback();

  const validateForm = () => {
    const newErrors = {};
    const now = DateTime.now().toUTC();
    const selectedDateTime = DateTime.fromISO(`${date}T${time}`).toUTC();

    const minutesDiff = selectedDateTime.diff(now, "minutes").toObject().minutes;
    const daysDiff = selectedDateTime.diff(now, "days").toObject().days;

    if (!phoneNumber) {
      newErrors.phoneNumber = "Phone number is required.";
    }

    if (phoneNumber && !parsePhoneNumber(phoneNumber, "GB")?.isValid()) {
      newErrors.phoneNumber = "Invalid phone number.";
    }

    if (!fileNumber) {
      newErrors.fileNumber = "File number is required.";
    }


    if (!date || !time) {
      newErrors.dateTime = "Both date and time are required.";
    }

    if (date && time && (minutesDiff < 5 || daysDiff > 6)) {
      newErrors.dateTime =
        "Date and time must be in the future, within the next 6 days.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await requestScheduledCallback({
        date,
        time,
        phoneNumber,
        fileNumber,
        accountType,
      });

      setDate("");
      setTime("");
      setPhoneNumber("");
      setFileNumber("");
      setAccountType("other");
      setErrors({});

    } catch (error) {
      console.error("Failed to submit callback:", error);
    }
  };


  return (
    <>
      <form onSubmit={handleSubmit}>
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button disabled={isLoading} variant="primary">
                Submit
              </Button>
            </SpaceBetween>
          }
          header={<Header variant="h1">Schedule Callback</Header>}
        >
          {(environment.AGENT_NOTE.length > 0 ? (
            <Alert type="info" header="Message for agent">
              {environment.AGENT_NOTE}
            </Alert>
          ) : null)}

          <Container header={<Header variant="h2">Callback Details</Header>}>
            <SpaceBetween direction="vertical" size="l">
              <FormField
                stretch={true}
                errorText={errors.dateTime}
              >
                <SpaceBetween size="s" direction="horizontal">
                  <FormField label="Callback Date">
                    <DatePicker
                      value={date}
                      onChange={({ detail }) => setDate(detail.value)}
                      placeholder="YYYY/MM/DD"
                    />
                  </FormField>
                  <FormField label="Callback Time">
                    <TimeInput
                      value={time}
                      onChange={({ detail }) => setTime(detail.value)}
                      format="hh:mm"
                      placeholder="hh:mm"
                    />
                  </FormField>
                </SpaceBetween>
              </FormField>
              {(environment.ACCOUNT_TYPE_VALUES.length > 0 ?
              <FormField label="Account type">
                <RadioGroup
                  onChange={({ detail }) => setAccountType(detail.value)}
                  value={accountType}
                    items={environment.ACCOUNT_TYPE_VALUES}
                />
              </FormField>
              :null)}

              <FormField
                label="Customer Phone Number"
                errorText={errors.phoneNumber}
                constraintText="Enter a valid phone number"
                validationState={errors.phoneNumber ? "error" : "none"}
              >
                <Input
                  value={phoneNumber}
                  onChange={({ detail }) => setPhoneNumber(detail.value)}
                  placeholder="Phone Number"
                />
              </FormField>

              <FormField
                label="File Number"
                errorText={errors.fileNumber}
                constraintText="Enter your file number"
                validationState={errors.fileNumber ? "error" : "none"}
              >
                <Input
                  value={fileNumber}
                  onChange={({ detail }) => setFileNumber(detail.value)}
                  placeholder="File Number"
                />
              </FormField>

            </SpaceBetween>
          </Container>
        </Form>
      </form>

      {response && (
        <Alert type={isError ? "error" : "success"} header={isError ? "Error submitting callback" : "Success"}>
          {typeof response === "string" ? response : JSON.stringify(response)}
        </Alert>
      )}
    </>
  );
}

export default ScheduleForm;
